/**
 * Circuit Breaker for storage sync network calls.
 *
 * Prevents continuous network thrashing and UI thread contention
 * when the remote storage/sync service is down or degraded.
 */

export type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

export interface CircuitBreakerOptions {
  /** Number of consecutive failures before opening circuit */
  failureThreshold?: number;
  /** Cooldown time in ms before transitioning from OPEN to HALF_OPEN */
  cooldownPeriodMs?: number;
  /** Maximum cooldown time when backoff increases */
  maxCooldownMs?: number;
  /** Callback fired when circuit state changes */
  onStateChange?: (state: CircuitState) => void;
}

/**
 * Token bucket rate limiter to cap total client requests per minute.
 */
export class TokenBucketRateLimiter {
  private tokens: number;
  private lastRefillTimestamp: number;
  private readonly maxTokens: number;
  private readonly refillRatePerSecond: number;

  constructor(options: { maxTokens?: number; refillRatePerSecond?: number } = {}) {
    this.maxTokens = options.maxTokens ?? 30; // Max burst of 30 requests
    this.refillRatePerSecond = options.refillRatePerSecond ?? 5; // Sustained ~300/min max
    this.tokens = this.maxTokens;
    this.lastRefillTimestamp = Date.now();
  }

  private refill(): void {
    const now = Date.now();
    const elapsedSeconds = (now - this.lastRefillTimestamp) / 1000;
    if (elapsedSeconds > 0) {
      this.tokens = Math.min(this.maxTokens, this.tokens + elapsedSeconds * this.refillRatePerSecond);
      this.lastRefillTimestamp = now;
    }
  }

  public tryAcquire(count = 1): boolean {
    this.refill();
    if (this.tokens >= count) {
      this.tokens -= count;
      return true;
    }
    return false;
  }

  public async acquire(count = 1): Promise<void> {
    while (!this.tryAcquire(count)) {
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }
}

export class CircuitBreaker {
  private state: CircuitState = 'CLOSED';
  private consecutiveFailures = 0;
  private nextAttemptTimestamp = 0;
  private currentCooldownMs: number;
  private readonly failureThreshold: number;
  private readonly initialCooldownMs: number;
  private readonly maxCooldownMs: number;
  private readonly onStateChange?: (state: CircuitState) => void;

  // Resource-specific cooldowns (e.g. documentId -> nextAllowedAttemptTimestamp)
  private resourceCooldowns = new Map<string, { failureCount: number; nextAttemptTimestamp: number }>();

  constructor(options: CircuitBreakerOptions = {}) {
    this.failureThreshold = options.failureThreshold ?? 3;
    this.initialCooldownMs = options.cooldownPeriodMs ?? 15_000;
    this.maxCooldownMs = options.maxCooldownMs ?? 60_000;
    this.currentCooldownMs = this.initialCooldownMs;
    this.onStateChange = options.onStateChange;
  }

  public getState(): CircuitState {
    if (this.state === 'OPEN' && Date.now() >= this.nextAttemptTimestamp) {
      this.setState('HALF_OPEN');
    }
    return this.state;
  }

  public isOpen(): boolean {
    return this.getState() === 'OPEN';
  }

  public isResourceBlocked(resourceKey: string): boolean {
    const entry = this.resourceCooldowns.get(resourceKey);
    if (!entry) return false;
    if (Date.now() >= entry.nextAttemptTimestamp) {
      return false;
    }
    return true;
  }

  public recordResourceSuccess(resourceKey: string): void {
    this.resourceCooldowns.delete(resourceKey);
  }

  public recordResourceFailure(resourceKey: string, cooldownMs = 15_000): void {
    const entry = this.resourceCooldowns.get(resourceKey) || { failureCount: 0, nextAttemptTimestamp: 0 };
    entry.failureCount++;
    const backoff = Math.min(cooldownMs * Math.pow(2, entry.failureCount - 1), 300_000); // max 5 min
    entry.nextAttemptTimestamp = Date.now() + backoff;
    this.resourceCooldowns.set(resourceKey, entry);
    console.warn(`[CircuitBreaker] Resource ${resourceKey} cooled off for ${Math.round(backoff / 1000)}s (fail #${entry.failureCount})`);
  }

  public recordSuccess(): void {
    this.consecutiveFailures = 0;
    this.currentCooldownMs = this.initialCooldownMs;
    if (this.state !== 'CLOSED') {
      console.log('[CircuitBreaker] Storage sync restored — Circuit CLOSED');
      this.setState('CLOSED');
    }
  }

  public recordFailure(isOfflineOr5xx: boolean): void {
    if (!isOfflineOr5xx) {
      // Non-rate-limit 4xx client errors (e.g. 400 Bad Request, 404 Not Found) don't trip the global circuit breaker
      return;
    }

    this.consecutiveFailures++;
    if (this.state === 'HALF_OPEN' || this.consecutiveFailures >= this.failureThreshold) {
      const cooldown = this.currentCooldownMs;
      this.nextAttemptTimestamp = Date.now() + cooldown;
      this.currentCooldownMs = Math.min(this.currentCooldownMs * 2, this.maxCooldownMs);
      console.warn(
        `[CircuitBreaker] Storage sync failure threshold reached (${this.consecutiveFailures}). Circuit OPEN for ${cooldown / 1000}s`
      );
      this.setState('OPEN');
    }
  }

  public async execute<T>(fn: () => Promise<T>, resourceKey?: string): Promise<T> {
    if (this.isOpen()) {
      const remainingSecs = Math.ceil((this.nextAttemptTimestamp - Date.now()) / 1000);
      throw new Error(`CircuitBreaker is OPEN — remote sync cooling off for ${remainingSecs}s`);
    }

    if (resourceKey && this.isResourceBlocked(resourceKey)) {
      const entry = this.resourceCooldowns.get(resourceKey)!;
      const remainingSecs = Math.ceil((entry.nextAttemptTimestamp - Date.now()) / 1000);
      throw new Error(`Resource ${resourceKey} is in backoff cooldown for ${remainingSecs}s`);
    }

    try {
      const result = await fn();
      this.recordSuccess();
      if (resourceKey) this.recordResourceSuccess(resourceKey);
      return result;
    } catch (err: unknown) {
      const status = (err as any)?.status;
      const is429 = status === 429 || (err instanceof Error && (err.message.includes('429') || err.message.toLowerCase().includes('rate limit') || err.message.toLowerCase().includes('too many requests')));
      const is4xx = typeof status === 'number' && status >= 400 && status < 500 && !is429;
      const isOfflineOr5xx =
        is429 ||
        (!is4xx &&
          (typeof status === 'number' && (status >= 500 || status === 0)) ||
          (err instanceof Error &&
            (err.message.toLowerCase().includes('offline') ||
              err.message.toLowerCase().includes('fetch') ||
              err.message.toLowerCase().includes('network') ||
              err.message.toLowerCase().includes('timeout') ||
              err.message.toLowerCase().includes('econnrefused'))));

      if (is429) {
        // Immediate backoff for rate limiting: trip circuit breaker immediately
        const cooldown = Math.max(this.currentCooldownMs, 30_000);
        this.nextAttemptTimestamp = Date.now() + cooldown;
        this.currentCooldownMs = Math.min(this.currentCooldownMs * 2, this.maxCooldownMs);
        this.setState('OPEN');
        console.warn(`[CircuitBreaker] Rate limit (HTTP 429) encountered. Circuit OPEN for ${cooldown / 1000}s`);
      } else {
        this.recordFailure(isOfflineOr5xx);
      }

      if (resourceKey && (isOfflineOr5xx || is429)) {
        this.recordResourceFailure(resourceKey, is429 ? 30_000 : 15_000);
      }
      throw err;
    }
  }

  public reset(): void {
    this.consecutiveFailures = 0;
    this.currentCooldownMs = this.initialCooldownMs;
    this.resourceCooldowns.clear();
    this.setState('CLOSED');
  }

  private setState(newState: CircuitState): void {
    if (this.state !== newState) {
      this.state = newState;
      this.onStateChange?.(newState);
    }
  }
}

export const syncCircuitBreaker = new CircuitBreaker();
export const globalStorageRateLimiter = new TokenBucketRateLimiter();
